// Shared "Confirmer + imprimer" action.
//
// Creates AMEEX parcels for the selected orders (POST /ameex/batch-confirm),
// generates the day's bon de livraison, then prints ALL the labels into a tab
// that was opened *synchronously* on the click (Chrome blocks window.open
// otherwise — bug Yahya hit 2026-06-11).
//
// Used by BOTH the Livraison AMEEX page ("Prêts à expédier") and the Orders
// page bulk bar, so there is a single source of truth: same endpoint, same
// print flow, and the ['ameex-dashboard'] cache is invalidated after every
// run so the AMEEX cockpit reflects an action taken from the Orders page (and
// the Orders list — which subscribes to realtime `orders` changes — reflects
// an action taken from the AMEEX page). One button, one behaviour, in sync.

import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ameexApi, ameexPrintApi, ordersApi, openLoadingTab } from '@/lib/api';
import { useT, type TFunc } from '@/lib/i18n';
import { AmeexCitySelect } from './AmeexCitySelect';

// Minimal order info the result UI needs to render a friendly fixable row
// (name to address the WhatsApp message + phone to open the chat). Both
// consumers (Orders page, AMEEX page) already hold full Order rows.
export type AmeexOrderInfo = {
  shopify_order_id: string;
  shopify_order_name?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
};

type BatchRes = Awaited<ReturnType<typeof ameexApi.batchConfirm>>;
type BatchResultRow = BatchRes['results'][number];
export type LastBatch = { ref: string; labelType: string } | null;

export function useAmeexConfirmPrint(onDone?: (res: BatchRes) => void) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState<string | null>(null);
  const [lastBatch, setLastBatch] = useState<LastBatch>(null);
  const [results, setResults] = useState<BatchResultRow[]>([]);
  // The exact id set of the most recent run — so "Revalider + imprimer" can
  // re-confirm + reprint the whole selection (now-fixed orders included).
  const [lastIds, setLastIds] = useState<string[]>([]);
  const printTabRef = useRef<Window | null>(null);

  const mut = useMutation({
    mutationFn: (ids: string[]) => ameexApi.batchConfirm(ids),
    onSuccess: (res, ids) => {
      setResults(res.results ?? []);
      setSummary(t('✓ {created} créé(s), {skipped} ignoré(s), {failed} échec(s)', { created: res.created, skipped: res.skipped, failed: res.failed }));
      const lb: LastBatch = res.batch_ref && res.delivery_note_created
        ? { ref: res.batch_ref, labelType: res.label_type }
        : null;
      setLastBatch(lb);
      // Print labels for ALL the selected orders that have a parcel (already-
      // confirmed + newly-confirmed) as ONE combined doc. The endpoint returns
      // a message page when nothing prints, so the tab is NEVER left closed.
      ameexPrintApi.labelsForOrdersInto(printTabRef.current, ids)
        .catch(() => { /* tab closed; fallback buttons remain */ });
      printTabRef.current = null;
      // Keep the AMEEX cockpit in sync no matter which page launched the batch.
      queryClient.invalidateQueries({ queryKey: ['ameex-dashboard'] });
      onDone?.(res);
    },
    onError: (e: Error) => {
      setSummary(t('Erreur: {message}', { message: e.message }));
      setResults([]);
      try { printTabRef.current?.close(); } catch { /* already closed */ }
      printTabRef.current = null;
    },
  });

  // Call this DIRECTLY inside the onClick — it opens the print tab within the
  // user gesture, then fires the batch.
  function run(ids: string[]) {
    if (ids.length === 0 || mut.isPending) return;
    setLastIds(ids);
    printTabRef.current = openLoadingTab(
      t('Création des colis AMEEX… les étiquettes s’ouvriront ici automatiquement.'),
    );
    mut.mutate(ids);
  }

  return { run, isPending: mut.isPending, summary, lastBatch, results, lastIds };
}

// The amber "Confirmer + imprimer N" button — identical markup wherever it is
// used. Presentational: drive it with a `useAmeexConfirmPrint` instance.
export function AmeexConfirmPrintButton({
  ids, run, isPending, className, label,
}: {
  ids: string[];
  run: (ids: string[]) => void;
  isPending: boolean;
  className?: string;
  /** The words on the button (the delivery page says « Créer les colis »). */
  label?: string;
}) {
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={() => run(ids)}
      disabled={ids.length === 0 || isPending}
      className={
        className ??
        'px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg disabled:opacity-50'
      }
    >
      {isPending ? t('Création + impression…') : (label ?? t('Confirmer + imprimer {n}', { n: ids.length || '' }))}
    </button>
  );
}

// What kind of fix a failed row needs — drives which inline control we show.
type FailKind = 'city' | 'address' | 'other';
function failKind(r: BatchResultRow): FailKind {
  if (r.status !== 'failed') return 'other';
  const e = r.error || '';
  if (/city/i.test(e)) return 'city';
  if (/address/i.test(e)) return 'address';
  return 'other';
}

// Turn a skipped/failed row into a human, actionable reason.
export function reasonLabel(r: BatchResultRow, t: TFunc): string {
  if (r.status === 'skipped') {
    if (r.reason === 'already_has_parcel') return t('Colis AMEEX déjà créé (commande déjà expédiée)');
    return r.reason;
  }
  if (r.status === 'failed') {
    const e = r.error || '';
    if (/city/i.test(e))    return t('Ville AMEEX manquante — ouvrez la commande et choisissez la ville');
    if (/address/i.test(e)) return t('Adresse de livraison manquante — ouvrez la commande');
    if (/COD/i.test(e))     return t('COD invalide (total − avance ≤ 0)');
    if (/not found/i.test(e)) return t('Commande introuvable');
    return e;
  }
  return '';
}

// Normalize a Moroccan phone to international digits for wa.me — mirrors
// AdminFollowUpPage.buildWaUrl (digits, then 0xxxxxxxxx → 212xxxxxxxxx).
function waUrlFor(phone: string | null | undefined, message: string): string | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (!digits) return null;
  const intl = digits.startsWith('0') ? `212${digits.slice(1)}` : digits;
  return `https://wa.me/${intl}?text=${encodeURIComponent(message)}`;
}

// One inline "fix" row for a FAILED order: friendly name + reason, plus the
// control matching the problem (city picker, or address input + WhatsApp ask).
// Saves through ordersApi.updateCustomerInfo — the same endpoint that already
// writes ameex_city_id; the address is folded into customer_note as
// "Adresse: <text>" so the AMEEX `add` reads it back on revalidate.
function FailFixRow({ row, info, t }: { row: BatchResultRow; info?: AmeexOrderInfo; t: TFunc }) {
  const kind = failKind(row);
  const name = (info?.customer_name ?? '').trim();
  const firstName = name.split(/\s+/)[0] || '';
  const label = info?.shopify_order_name || `#${row.shopify_order_id}`;

  const [savedCity, setSavedCity] = useState(false);
  const [cityId, setCityId] = useState('');
  const [address, setAddress] = useState('');
  const [addrSaved, setAddrSaved] = useState(false);

  const saveCity = useMutation({
    mutationFn: (id: string) =>
      ordersApi.updateCustomerInfo(row.shopify_order_id, { ameex_city_id: parseInt(id, 10) }),
    onSuccess: () => setSavedCity(true),
    onError: (e: Error) => alert(e.message),
  });
  const saveAddress = useMutation({
    mutationFn: (text: string) =>
      ordersApi.updateCustomerInfo(row.shopify_order_id, { customer_note: `Adresse: ${text.trim()}` }),
    onSuccess: () => setAddrSaved(true),
    onError: (e: Error) => alert(e.message),
  });

  const askMsg = t(
    'Bonjour {name}, pour préparer votre livraison Storiz, pouvez-vous me communiquer votre adresse complète (quartier, rue, repère) ? Merci 🙏',
    { name: firstName || t('cher client') },
  );
  const askUrl = waUrlFor(info?.customer_phone, askMsg);

  return (
    <li className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5 space-y-2">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-semibold text-gray-800">{label}</span>
        {name && <span className="text-[11px] text-gray-500">{name}</span>}
        <span className="text-[11px] text-amber-700">— {reasonLabel(row, t)}</span>
      </div>

      {kind === 'city' && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="min-w-[200px] flex-1">
            <AmeexCitySelect
              value={cityId}
              onChange={(next) => { setCityId(next.id); setSavedCity(false); saveCity.mutate(next.id); }}
              placeholder={t('Choisir la ville AMEEX…')}
            />
          </div>
          {saveCity.isPending && <span className="text-[11px] text-gray-400">{t('Enregistrement…')}</span>}
          {savedCity && !saveCity.isPending && <span className="text-[11px] font-medium text-green-600">✓ {t('Ville enregistrée')}</span>}
        </div>
      )}

      {kind === 'address' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={address}
              onChange={(e) => { setAddress(e.target.value); setAddrSaved(false); }}
              placeholder={t('Adresse complète (quartier, rue, repère)')}
              className="flex-1 min-w-[200px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              type="button"
              onClick={() => address.trim() && saveAddress.mutate(address)}
              disabled={!address.trim() || saveAddress.isPending}
              className="px-3 py-2 rounded-lg text-xs font-semibold text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50"
            >
              {saveAddress.isPending ? t('Enregistrement…') : t('Enregistrer')}
            </button>
            {addrSaved && !saveAddress.isPending && <span className="text-[11px] font-medium text-green-600">✓ {t('Adresse enregistrée')}</span>}
          </div>
          {askUrl && (
            <button
              type="button"
              onClick={() => window.open(askUrl, '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[rgba(37,211,102,.5)] text-[#0f7a3d] dark:text-status-done"
            >
              💬 {t('Demander l’adresse')}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// The result line: summary + reprint / bordereau fallbacks, plus — when the
// batch had FAILURES — inline fix rows (city / address) and a one-click
// "Revalider + imprimer" that re-confirms the whole (now-fixed) selection.
// The print tab pops automatically; the reprint buttons are manual re-opens.
export function AmeexBatchResult({
  summary, lastBatch, results = [], orders = [], onRevalidate, isPending = false,
}: {
  summary: string | null;
  lastBatch: LastBatch;
  results?: BatchResultRow[];
  /** Order rows for the failed ids, so we can show name + open WhatsApp. */
  orders?: AmeexOrderInfo[];
  /** Re-run the hook (re-confirm + reprint) over the full last selection. */
  onRevalidate?: () => void;
  isPending?: boolean;
}) {
  const { t } = useT();
  if (!summary) return null;

  const infoById = new Map(orders.map((o) => [o.shopify_order_id, o]));
  const failures = results.filter((r) => r.status === 'failed');

  // Skipped rows aren't fixable inline — keep the compact grouped summary.
  const skippedIssues = new Map<string, number>();
  for (const r of results) {
    if (r.status !== 'skipped') continue;
    const label = reasonLabel(r, t);
    skippedIssues.set(label, (skippedIssues.get(label) ?? 0) + 1);
  }

  return (
    <div className="text-xs text-gray-700 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-medium">{summary}</span>
        {lastBatch && (
          <>
            <button
              type="button"
              onClick={() => ameexPrintApi.labelsByRef(lastBatch.ref, lastBatch.labelType).catch((e: Error) => alert(e.message))}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #C9A24A, #A8842F)' }}
            >
              🖨 {t('Imprimer les étiquettes')}
            </button>
            <button
              type="button"
              onClick={() => ameexPrintApi.manifestByRef(lastBatch.ref).catch((e: Error) => alert(e.message))}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[rgba(201,162,74,.4)] text-adm-gold-ink"
            >
              📋 {t('Bordereau')}
            </button>
          </>
        )}
      </div>

      {skippedIssues.size > 0 && (
        <ul className="text-[11px] text-gray-600 space-y-0.5 pl-0.5">
          {Array.from(skippedIssues.entries()).map(([label, n]) => (
            <li key={label} className="flex gap-1.5">
              <span className="text-amber-600">•</span>
              <span><strong>{n}</strong> — {label}</span>
            </li>
          ))}
        </ul>
      )}

      {failures.length > 0 && (
        <div className="space-y-2 pt-1">
          <p className="text-[11px] font-medium text-gray-600">
            {t('Corrigez les commandes en échec, puis revalidez :')}
          </p>
          <ul className="space-y-2">
            {failures.map((r) => (
              <FailFixRow key={r.shopify_order_id} row={r} info={infoById.get(r.shopify_order_id)} t={t} />
            ))}
          </ul>
          {onRevalidate && (
            <button
              type="button"
              onClick={onRevalidate}
              disabled={isPending}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {isPending ? t('Revalidation…') : t('Revalider + imprimer')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
